// ID 按字符串处理，避免雪花 ID 转成 Number 后丢失精度。
export const skuKey = ids => String(ids || '').split(':').filter(Boolean).sort().join(':')

function combinations(specList) {
  if (!specList.length || specList.some(spec => !spec.specValueList || !spec.specValueList.length)) return []
  return specList.reduce((rows, spec) => rows.reduce((next, row) =>
    next.concat(spec.specValueList.map(value => [...row, value])), []), [[]])
}

export function buildSkuList(specList, oldList) {
  const oldMap = new Map()
  oldList.forEach(sku => {
    const key = skuKey(sku.specValueIds)
    if (oldMap.has(key)) throw new Error('存在重复的 SKU 规格组合，请检查规格数据')
    oldMap.set(key, sku)
  })
  const seen = new Set()
  return combinations(specList).map(values => {
    const specValueIds = values.map(value => String(value.id)).join(':')
    const key = skuKey(specValueIds)
    if (seen.has(key)) throw new Error('规格值 ID 重复，请检查规格数据')
    seen.add(key)
    const old = oldMap.get(key)
    const picture = values.reduce((pic, value) => value.picture || pic, '')
    return {
      skuCode: null, price: null, costPrice: 0, memberPrice: 0, groupPrice: 0,
      pointPrice: 0, activityPrice: 0, seckillPrice: 0, stock: 0, weight: 0,
      ...old,
      skuName: values.map(value => value.specValueName).join('  '),
      specValueIds,
      picture: (old && old.picture) || picture
    }
  })
}

export function validateSkuList(specList, skuList) {
  const ids = new Set()
  if (!specList.length || specList.some(spec => !spec.specValueList || !spec.specValueList.length)) {
    return '请为每个规格添加规格值并生成 SKU'
  }
  for (const spec of specList) {
    for (const value of spec.specValueList) {
      if (value.id == null || ids.has(String(value.id))) return '规格值 ID 重复或缺失，请检查规格数据'
      ids.add(String(value.id))
      if (!String(value.specValueName || '').trim()) return '规格值不能为空'
    }
  }
  const expected = new Set(combinations(specList).map(values => skuKey(values.map(value => value.id).join(':'))))
  const actual = new Set()
  for (const sku of skuList) {
    const values = String(sku.specValueIds || '').split(':')
    if (values.length !== specList.length || values.some(id => !id)) return 'SKU 规格组合不合法，请重新生成 SKU'
    const key = skuKey(sku.specValueIds)
    if (!expected.has(key) || actual.has(key)) return 'SKU 规格组合重复或已变更，请重新生成 SKU 并检查价格'
    actual.add(key)
  }
  if (actual.size !== expected.size) return 'SKU 规格组合不完整，请生成 SKU 并填写各规格价格'
  return ''
}

const numeric = value => value !== null && value !== undefined && String(value).trim() !== '' && Number.isFinite(Number(value))

export default {
  data() {
    return { specLocalId: 0 }
  },
  methods: {
    nextSpecId() {
      // 会话内单调递减，删除后不复用；也不对后台的长整型 ID 做运算。
      const used = new Set()
      this.form.specList.forEach(spec => {
        used.add(String(spec.id))
        spec.specValueList.forEach(value => used.add(String(value.id)))
      })
      do { this.specLocalId-- } while (used.has(String(this.specLocalId)))
      return this.specLocalId
    },
    generateSkus() {
      this.skuLoading = true
      try {
        const oldList = this.form.skuList || []
        const nextList = buildSkuList(this.form.specList, oldList)
        const oldKeys = new Set(oldList.map(sku => skuKey(sku.specValueIds)))
        if (oldList.length !== nextList.length || nextList.some(sku => !oldKeys.has(skuKey(sku.specValueIds)))) {
          this.isChangeSpec = true
        }
        this.form.skuList = nextList
      } catch (error) {
        this.$message.error(error.message)
      } finally {
        this.skuLoading = false
      }
    },
    editSpec() {
      this.specDisable = false
    },
    addSpec() {
      if (!this.validatorSpecValueAll()) return
      this.form.specList.push({
        id: this.nextSpecId(), specName: null, specDesc: null, isHaveImg: false,
        sort: this.form.specList.length + 1, specValueList: []
      })
      this.isChangeSpec = true
    },
    delSpec(index) {
      if (this.specDisable) return
      this.form.specList.splice(index, 1)
      this.isChangeSpec = true
      this.generateSkus()
    },
    addSpecValue(item) {
      if (!this.validatorSpecValue(item)) return
      const id = this.nextSpecId()
      item.specValueList.push({
        id, index: id, specValueName: null, specName: item.specName, picture: null,
        isVisible: true, specErrorMessage: undefined, sort: 0
      })
      this.isChangeSpec = true
    },
    delSpecValue(item, record) {
      if (this.specDisable) return
      item.specValueList = item.specValueList.filter(value => value.id !== record.id)
      this.isChangeSpec = true
      this.generateSkus()
    },
    multipleSpecChange() {
      this.form.specList = []
      this.form.skuList = []
      this.specDisable = false
      this.isChangeSpec = true
    },
    syncSkuMetadata() {
      // 只在组合校验通过后同步名称，不能在提交时补出未经确认的价格。
      if (this.form.isMultipleSpec) {
        this.form.skuList = buildSkuList(this.form.specList, this.form.skuList)
      }
    },
    validatorSku() {
      if (!this.form.isMultipleSpec) return true
      let message = validateSkuList(this.form.specList || [], this.form.skuList || [])
      if (!message) {
        for (const [index, sku] of this.form.skuList.entries()) {
          const checks = [
            ['price', '价格', 0], ['stock', '库存', 1], ['weight', '重量', 0]
          ]
          const invalid = checks.find(([field, , min]) => !numeric(sku[field]) || Number(sku[field]) < min ||
            (field === 'weight' && Number(sku[field]) === 0)) ||
            [['activityPrice', '活动价格'], ['costPrice', '成本价']].find(([field]) =>
              sku[field] != null && sku[field] !== '' && (!numeric(sku[field]) || Number(sku[field]) < 0))
          if (invalid) {
            message = `第 ${index + 1} 个 SKU（${sku.skuName}）的${invalid[1]}未填写或不合法`
            break
          }
        }
      }
      this.skuErrorMessage = message
      return !message
    }
  }
}
